import React, { useRef, useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import './Contact.css';
import emailjs from '@emailjs/browser';

function Contact() {
  const form = useRef();
  const [searchParams] = useSearchParams();
  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [focusedField, setFocusedField] = useState(null);
  const [selectedTopic, setSelectedTopic] = useState('');

  // Animation trigger for scroll reveal
  const [isVisible, setIsVisible] = useState(false);
  const containerRef = useRef(null);
  
  // Check for topic parameter in URL and pre-select
  useEffect(() => {
    const topicParam = searchParams.get('topic');
    if (topicParam === 'outplacement') {
      setSelectedTopic('Outplacement Services');
    }
  }, [searchParams]);
  
  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
          observer.unobserve(entry.target);
        }
      },
      { threshold: 0.1 }
    );
    
    if (containerRef.current) {
      observer.observe(containerRef.current);
    }
    
    return () => {
      if (containerRef.current) {
        observer.unobserve(containerRef.current);
      }
    };
  }, []);

  const validate = (data) => {
    const errs = {};
    if (!data.name) errs.name = "Name is required";
    if (!data.email || !/^\S+@\S+\.\S+$/.test(data.email))
      errs.email = "Valid email required";
    if (!data.phone || !/^\d{10,15}$/.test(data.phone))
      errs.phone = "Valid phone number required";
    if (!data.topic) errs.topic = "Topic is required";
    if (!data.message) errs.message = "Message is required";
    return errs;
  };

  const sendEmail = (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    
    const formData = {
      name: form.current.name.value,
      email: form.current.email.value,
      phone: form.current.phone.value,
      topic: form.current.topic.value,
      message: form.current.message.value,
    };

    const validationErrors = validate(formData);
    setErrors(validationErrors);
    
    if (Object.keys(validationErrors).length) {
      form.current.classList.add('form-shake');
      setTimeout(() => {
        form.current.classList.remove('form-shake');
      }, 500);
      
      setIsSubmitting(false);
      return;
    }

    // Choose template based on topic
    const templateId = formData.topic === 'Outplacement Services' 
      ? 'template_outplacement'  // Replace with your actual Outplacement template ID
      : 'template_xztv4vl';       // Your existing general template ID

    emailjs
      .sendForm(
        'service_d2wi73c',
        templateId,
        form.current,
        'Ltfk9jhyAX1zQkBnR'
      )
      .then(
        () => {
          setIsSubmitting(false);
          setSubmitted(true);
          form.current.reset();
          setSelectedTopic('');
          setErrors({});
          setTimeout(() => setSubmitted(false), 5000);
        },
        () => {
          setIsSubmitting(false);
          alert("Failed to send message. Please try again later.");
        }
      );
  };

  const handleFocus = (field) => {
    setFocusedField(field);
  };

  const handleBlur = () => {
    setFocusedField(null);
  };

  const handleTopicChange = (e) => {
    setSelectedTopic(e.target.value);
  };

  return (
    <div ref={containerRef} className={`contact-container ${isVisible ? 'visible' : ''}`}>
      {/* Animated background elements */}
      <div className="animated-background">
        <span></span>
        <span></span>
        <span></span>
        <span></span>
      </div>
      
      <h2 className="contact-title">Contact Us</h2>
      <p className="contact-subtitle">
        Fill out the form below and we'll get back to you as soon as possible.
      </p>
      
      {submitted && (
        <div className="success-message">
          <svg viewBox="0 0 24 24" className="success-icon">
            <path fill="currentColor" d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-2 15l-5-5 1.41-1.41L10 14.17l7.59-7.59L19 8l-9 9z" />
          </svg>
          <p>Message sent successfully! We'll be in touch soon.</p>
        </div>
      )}
      
      <form ref={form} onSubmit={sendEmail} className="contact-form">
        <div className="form-row">
          <div className={`field-group ${focusedField === 'name' ? 'focused' : ''}`}>
            <label htmlFor="name">Name</label>
            <input 
              id="name" 
              type="text" 
              name="name" 
              placeholder="Your full name" 
              onFocus={() => handleFocus('name')}
              onBlur={handleBlur}
            />
            {errors.name && <span className="error">{errors.name}</span>}
          </div>
          
          <div className={`field-group ${focusedField === 'email' ? 'focused' : ''}`}>
            <label htmlFor="email">Email</label>
            <input 
              id="email" 
              type="email" 
              name="email" 
              placeholder="your@email.com" 
              onFocus={() => handleFocus('email')}
              onBlur={handleBlur}
            />
            {errors.email && <span className="error">{errors.email}</span>}
          </div>
        </div>
        
        <div className="form-row">
          <div className={`field-group ${focusedField === 'phone' ? 'focused' : ''}`}>
            <label htmlFor="phone">Phone Number</label>
            <input 
              id="phone" 
              type="tel" 
              name="phone" 
              placeholder="Your phone number" 
              onFocus={() => handleFocus('phone')}
              onBlur={handleBlur}
            />
            {errors.phone && <span className="error">{errors.phone}</span>}
          </div>
          
          <div className={`field-group ${focusedField === 'topic' ? 'focused' : ''}`}>
            <label htmlFor="topic">Topic</label>
            <select 
              id="topic" 
              name="topic" 
              value={selectedTopic}
              onChange={handleTopicChange}
              onFocus={() => handleFocus('topic')}
              onBlur={handleBlur}
            >
              <option value="">Choose topic</option>
              <option value="General Inquiry">General Inquiry</option>
              <option value="Billing">Billing</option>
              <option value="Support">Custom Plan Request</option>
              <option value="Partnership">Partnership</option>
              <option value="Outplacement Services">Outplacement Services</option>
              <option value="Other">Other</option>
            </select>
            {errors.topic && <span className="error">{errors.topic}</span>}
          </div>
        </div>
        
        <div className={`field-group full-width ${focusedField === 'message' ? 'focused' : ''}`}>
          <label htmlFor="message">Message</label>
          <textarea 
            id="message" 
            name="message" 
            rows="5" 
            placeholder="Please provide details about your inquiry..." 
            onFocus={() => handleFocus('message')}
            onBlur={handleBlur}
          />
          {errors.message && <span className="error">{errors.message}</span>}
        </div>
        
        <div className="button-wrapper">
          <button type="submit" disabled={isSubmitting} className={isSubmitting ? 'submitting' : ''}>
            {isSubmitting ? (
              <>
                <span className="spinner"></span>
                Sending...
              </>
            ) : (
              'Send Message'
            )}
          </button>
        </div>
      </form>
    </div>
  );
}

export default Contact;